// ── Conversation threading + bulk-action concurrency for pages/Mail.jsx ─────
// Pure — no React, no network, no DOM — so both are unit-testable on their
// own and the page component only ever calls them.

// A back-and-forth is grouped into ONE row instead of a flat list of every
// reply. Grouping only sees the messages already on the current page — the
// API's message projection already carries each message's own
// `conversationId` (api/src/mailClient.js's MESSAGE_LIST_SELECT), so nothing
// new is fetched; a thread whose older half fell off an earlier page simply
// shows what's loaded, the same way any other list paginates. Messages with
// no conversationId (shouldn't happen, but never trusted) are their own
// singleton group.
//
// THE LIST ARRIVES NEWEST-FIRST (or Graph-relevance-ordered for a search), so
// a group's position is anchored at its FIRST appearance — its newest
// message — and every later message sharing that conversationId joins it
// there rather than at its own (older) position. Within a group, the
// messages are kept in the order they arrived (newest first), which is how a
// thread reads top-to-bottom in Outlook's own list.
export function groupByConversation(messages) {
  const order = [];
  const byKey = new Map();
  let anon = 0;
  for (const m of (messages || [])) {
    const key = m.conversationId || `__solo_${anon++}`;
    let g = byKey.get(key);
    if (!g) {
      g = { key, subject: m.subject, items: [] };
      byKey.set(key, g);
      order.push(g);
    }
    g.items.push(m);
  }
  return order;
}

// ── Bounded-concurrency bulk actions ─────────────────────────────────────────
// A multi-select delete/move/mark-read is one Graph call PER MESSAGE — there
// is no batch endpoint wired up for mail — so a large selection run fully
// sequential would be slow, and run fully in parallel would be a burst that
// invites throttling. `concurrency` workers each pull from a shared queue
// until it's empty; `fn` failures are the caller's problem (each worker keeps
// going regardless, so one bad id can't stop the rest of the batch).
export async function runInChunks(items, concurrency, fn) {
  const queue = [...items];
  const workers = Array.from({ length: Math.max(1, Math.min(concurrency, queue.length)) }, async () => {
    while (queue.length) {
      const item = queue.shift();
      await fn(item);
    }
  });
  await Promise.all(workers);
}
